import type { Checkbox, ItemNode, Urgency } from '@/types/domain';

// Клиентское зеркало backend tree.util + rank.util — для оптимистичных правок дерева.
// После ack сервера дерево всё равно реконсилится рефетчем, но локально считаем сразу.

export const RANK_GAP = 1000;

export function computeRank(prev: number | null, next: number | null): number {
  if (prev == null && next == null) return RANK_GAP;
  if (prev == null) return Math.floor((next as number) / 2);
  if (next == null) return prev + RANK_GAP;
  return Math.floor((prev + next) / 2);
}

export function needsRebalance(prev: number | null, next: number | null): boolean {
  if (prev == null || next == null) return false;
  return next - prev <= 1;
}

export function cloneTree(items: ItemNode[]): ItemNode[] {
  // JSON-клон: узлы — чистые данные (string/number/boolean/array), Date нет.
  // structuredClone не годится — Vue-reactive proxy не клонируется.
  return JSON.parse(JSON.stringify(items)) as ItemNode[];
}

/** Пересчёт rollup/checkbox/done снизу вверх (мутирует дерево на месте). */
export function recompute(items: ItemNode[]): ItemNode[] {
  const visit = (node: ItemNode): { done: number; total: number } => {
    if (node.children.length === 0) {
      node.rollup = { doneLeaves: node.done ? 1 : 0, totalLeaves: 1 };
      node.checkbox = node.done ? 'done' : 'empty';
      return { done: node.rollup.doneLeaves, total: 1 };
    }
    let done = 0;
    let total = 0;
    for (const c of node.children) {
      const r = visit(c);
      done += r.done;
      total += r.total;
    }
    node.rollup = { doneLeaves: done, totalLeaves: total };
    node.done = total > 0 && done === total;
    node.checkbox = done === 0 ? 'empty' : done === total ? 'done' : 'partial';
    return { done, total };
  };
  for (const n of items) visit(n);
  return items;
}

export function findNode(items: ItemNode[], id: string): ItemNode | null {
  for (const n of items) {
    if (n.id === id) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
}

/** Массив-сиблингов, содержащий узел id (children родителя или корень). */
export function siblingsOf(items: ItemNode[], id: string): ItemNode[] | null {
  if (items.some((n) => n.id === id)) return items;
  for (const n of items) {
    const r = siblingsOf(n.children, id);
    if (r) return r;
  }
  return null;
}

export function parentOf(items: ItemNode[], id: string): ItemNode | null {
  for (const n of items) {
    if (n.children.some((c) => c.id === id)) return n;
    const r = parentOf(n.children, id);
    if (r) return r;
  }
  return null;
}

/** Удаляет поддерево по id, возвращает удалённый узел (или null). */
export function removeNode(items: ItemNode[], id: string): ItemNode | null {
  const idx = items.findIndex((n) => n.id === id);
  if (idx >= 0) {
    const [node] = items.splice(idx, 1);
    return node;
  }
  for (const n of items) {
    const r = removeNode(n.children, id);
    if (r) return r;
  }
  return null;
}

/** Каскад done на узел и всех потомков. */
export function setSubtreeDone(node: ItemNode, done: boolean): void {
  node.done = done;
  for (const c of node.children) setSubtreeDone(c, done);
}

/** Вставка узла в массив сиблингов после afterId (или в конец), назначает position. */
export function insertAfter(siblings: ItemNode[], node: ItemNode, afterId: string | null): void {
  let idx: number;
  if (!afterId) idx = siblings.length - 1;
  else {
    idx = siblings.findIndex((s) => s.id === afterId);
    if (idx === -1) idx = siblings.length - 1;
  }
  const prev = siblings[idx]?.position ?? null;
  const next = siblings[idx + 1]?.position ?? null;
  if (needsRebalance(prev, next)) {
    siblings.forEach((s, i) => (s.position = (i + 1) * RANK_GAP));
    const p = idx >= 0 ? siblings[idx].position : null;
    const n = siblings[idx + 1]?.position ?? null;
    node.position = computeRank(p, n);
  } else {
    node.position = computeRank(prev, next);
  }
  siblings.splice(idx + 1, 0, node);
}

export function makeLeaf(id: string, text: string, urgency: Urgency, depth: number): ItemNode {
  return {
    id,
    parentId: null,
    depth,
    position: 0,
    text,
    urgency,
    done: false,
    version: 0,
    hasAttachments: false,
    assigneeIds: [],
    checkedBy: [],
    rollup: { doneLeaves: 0, totalLeaves: 1 },
    checkbox: 'empty' as Checkbox,
    children: [],
  };
}

export function flatten(items: ItemNode[]): ItemNode[] {
  const out: ItemNode[] = [];
  const walk = (ns: ItemNode[]) => {
    for (const n of ns) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(items);
  return out;
}
